// React root — owns the overlay tree on top of the AR canvas. Subscribes
// to WheelLoader + WheelPlacementController state via useSyncExternalStore;
// XR8 startup is deferred to a tap (browsers require a user gesture before
// getUserMedia), which also keeps the headless smoke test green.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
} from 'react'
import {
  TopControlsView,
  CircleIconButton,
} from '@/components/top-controls-view/top-controls-view'
import { WheelSelector } from '@/components/wheel-selector/wheel-selector'
import { Camera, Settings, Trash2, X } from 'lucide-react'

import { ManualAdjustmentPanel } from '@/components/manual-adjustment-panel/manual-adjustment-panel'
import { ManualAdjustmentPanelV2 } from '@/components/manual-adjustment-panel/manual-adjustment-panel-v2'
import { InstructionOverlay } from '@/components/instruction-overlay/instruction-overlay'
import { CoachingOverlay } from '@/components/coaching-overlay/coaching-overlay'
import { computeTrackingConvergence } from '@/ar/tracking-convergence'
import { GAUGE_LINE_COLOR_HEX } from '@/ar/depth-delta-visualizer'
import { computeElementRotationDeg } from '@/lib/computeElementRotation'
import type { WebXRSessionInfo } from '@/ar/webxr-driver'
import { DiagnosticsHUD } from '@/components/diagnostics-hud/diagnostics-hud'
import {
  VersionBadge,
  BUILD_STAMP_ANCHOR,
} from '@/components/version-badge/version-badge'
import { SettingsPopover } from '@/components/settings-popover/settings-popover'
import type { WheelAdjustModeStore } from '@/models/wheel-adjust-mode'
import { Toast } from '@/components/toast/toast'
import type { TrackingDiagnostics } from '@/ar/real-xr8-driver'
import type { WheelLoader, WheelLoaderState } from '@/services/wheel-loader'
import { afterNextPaint } from '@/lib/afterNextPaint'
import { loadFailureMessage } from '@/services/wheel-loader'
import type {
  WheelPlacementController,
  WheelPlacementControllerState,
} from '@/controllers/wheel-placement-controller'
import { DEFAULT_MAX_WHEELS as MAX_WHEELS } from '@/controllers/wheel-placement-controller'
import type { WheelRenderer } from '@/ar/wheel-renderer'
import type { ARSessionState } from '@/models/placement-state'
import { readDomAngle } from '@/lib/readDomAngle'
import { atoLog } from '@/services/ato-log'
import { projectTopControlsWheelInfo } from '@/components/top-controls-view/project-top-controls-wheel-info'
import { IosUiOverlay } from '@/components/ios-ui/ios-ui-overlay/ios-ui-overlay'
import type { IosPlacementController, IosPlacementState } from '@/controllers/ios-placement-controller'
import { MAX_CAMBER_ANGLE_RAD } from '@/controllers/wheel-placement-controller'
import { MANUAL_ADJUSTMENT_STEPS } from '@/models/placement-state'
import { buildArCompleteUrl } from '@/lib/buildArCompleteUrl'

interface ToastStoreLike {
  getState(): string | null
  subscribe(listener: () => void): () => void
  dismiss(): void
}

interface DiagnosticsStoreLike {
  getState(): TrackingDiagnostics
  subscribe(listener: () => void): () => void
}

import { ArSplash } from '@/components/ar-splash/ar-splash'
import { createSplashStore, type SplashStore } from '@/models/splash-store'
import type { SplashBrand } from '@/services/fetch-wheel-brand'

/** Without a store (tests, ?uiTest) the splash offers Start AR and no brand. */
const DEFAULT_SPLASH_STORE: SplashStore = createSplashStore({ mode: { kind: 'start' }, brand: null })

interface AppProps {
  loader: WheelLoader
  controller: WheelPlacementController
  /** Renderer is needed for AR-session-state subscription so the
   *  coaching overlay can gate placement UI on SLAM convergence. */
  renderer: WheelRenderer
  onStartAR: () => Promise<void>
  onClose: () => void
  toastStore: ToastStoreLike
  /** Dev-only tracking-diagnostics surface. When set, renders the HUD in
   *  the bottom-right. main.ts only passes this in `import.meta.env.DEV`. */
  diagnosticsStore?: DiagnosticsStoreLike
  /** Plane-mode debug: hide wheel-placement overlays (tap hint, loading
   *  hint, coaching overlay) so only the raw camera feed + green plane
   *  polygons are visible. Enabled via ?planeMode=1 URL param. */
  hideWheelUI?: boolean
  /** Optional driver-sourced session info snapshot getter. When set,
   *  drives the coaching-overlay progress bar + hint text via
   *  computeTrackingConvergence. main.ts passes a lambda that returns
   *  driver.sessionInfo (WebXR only; legacy XR8 has no equivalent). */
  getSessionInfo?: () => WebXRSessionInfo
  /** Optional escape hatch — coaching overlay renders a "Place anyway"
   *  button after PLACE_ANYWAY_UNLOCK_MS if this is set. Wired to
   *  driver.forceReady() in main.ts. */
  onForceReady?: () => void
  /** Debug-only. When set (main.ts passes when ?trackToggle is present),
   *  the v2 floating row shows a gear icon that opens a config popover
   *  with PREV / PLANE cycle buttons. Consolidates the previous
   *  separate corner overlays into one entry point. */
  getTrackMode?: () => 'plane' | 'world'
  setTrackMode?: (mode: 'plane' | 'world') => void
  getPreviewMode?: () => 'camera-locked' | 'plane-anchored'
  setPreviewMode?: (mode: 'camera-locked' | 'plane-anchored') => void
  /** ?groundLine=1: render a screen-locked blue reference line and
   *  put the renderer into groundLine mode so the first tap derives
   *  a fixed ground Y (subsequent taps land at that same Y). */
  useGroundLine?: boolean
  /** ?uiTest=1: skip the entire AR flow (StartAROverlay, WebXR
   *  session, coaching/loading gates) and jump straight to the
   *  arStarted=true state with a fake wheel selected. Purely for
   *  iterating on the overlay UI without triggering AR every reload.
   *  All controller calls become no-ops. */
  useUiTest?: boolean
  /** How a placed wheel is adjusted. Touch is the default; the gear
   *  popover switches to the arrow pad. Was the ?touchUi flag. */
  wheelAdjustMode?: WheelAdjustModeStore
  /** Takes an AR photo. Provided by main.ts, which owns the driver. */
  onCamera?: () => void
  /** ?orHud: show the orientation debug badge (DOM signal / DOE
   *  signal / screen.orientation.angle) in the bottom-right corner. */
  showOrientationHud?: boolean
  /** The splash's mode (from the boot capability probe) and the wheel's
   *  brand (from the parts API by the launch URL's wheel id). */
  splashStore?: SplashStore
  /** ?use_ios_ui (2026-10-17) — render the iOS 0.7.0 overlay tree instead
   *  of the shipped one. Needs `iosController`; absent, nothing changes. */
  useIosUi?: boolean
  iosController?: IosPlacementController
  /** Where the iOS UI's ways out go: the invocation's return URL (with the
   *  ar-complete fragment), else the client host as given; null = nowhere. */
  exit?: { url: URL; isReturnURL: boolean } | null
  /** Navigation seam for the iOS UI's exits; tests pass a spy. */
  navigate?: (url: string) => void
  /** The iOS load cards' Retry and Cancel. */
  onRetryLoad?: () => void
  onCancelLoad?: () => void
  /** The quarter turn between the DOM and the rendered frame — the iOS
   *  pad's arrows follow the user's view. Absent: no rotation. */
  getRenderedQuadrant?: () => 0 | 90 | 180 | 270
}

export function App({
  loader,
  controller,
  renderer,
  onStartAR,
  onClose,
  toastStore,
  diagnosticsStore,
  hideWheelUI,
  getSessionInfo,
  onForceReady,
  getTrackMode,
  setTrackMode,
  getPreviewMode,
  setPreviewMode,
  useGroundLine = false,
  useUiTest = false,
  wheelAdjustMode,
  onCamera,
  showOrientationHud = false,
  splashStore = DEFAULT_SPLASH_STORE,
  useIosUi = false,
  iosController,
  exit = null,
  navigate = (url: string) => window.location.assign(url),
  onRetryLoad,
  onCancelLoad,
  getRenderedQuadrant = () => 0,
}: AppProps) {
  const loaderState = useExternalStore(loader)
  const controllerState = useControllerStore(controller)
  const useControlChips = useMemo(() => {
    try {
      return new URL(window.location.href).searchParams.has('controlChips')
    } catch {
      return false
    }
  }, [])
  const sessionState = useSessionState(renderer)
  const toastMessage = useToastStore(toastStore)
  const iosState = useIosPlacementState(iosController)
  const viewportSize = useViewportSize(useIosUi)
  const isLandscape = useIsLandscape()
  const physRotation = useDevicePhysicalRotation()
  const doeIsLandscape = physRotation == null ? null : physRotation !== 0
  const domRotation = useDomRotation()
  // In-AR in-place element rotation. 0 in browser mode (DOM rotates
  // with the phone so the two signals agree); ±90 during an AR session
  // when the pinned DOM no longer faces the viewer.
  const elRotDeg = computeElementRotationDeg(physRotation, domRotation)
  // Landscape-state diagnostic — logs every DOM-signal flip with all
  // four underlying signals. The DOE hook logs its own transitions
  // separately (with gamma) inside useDeviceOrientationLandscape.
  useEffect(() => {
    const so = (screen as unknown as { orientation?: { type?: string } })
      .orientation
    // eslint-disable-next-line no-console
    console.log('[ATO] isLandscape:', isLandscape, 'domRotation:', domRotation, {
      innerW: window.innerWidth,
      innerH: window.innerHeight,
      screenOrientation: so?.type,
      matchesLandscape: window.matchMedia?.('(orientation: landscape)')
        ?.matches,
    })
  }, [isLandscape, domRotation])

  // Three fixed-positioned containers replace the old full-page
  // wrapper. Each container computes its OWN position + dimensions +
  // rotation from `domRotation` so it always lands at its designated
  // chassis-relative corner (TL / TR / B). Content inside a container
  // stays portrait-canonical; the container itself does the rotate
  // needed for landscape hold.
  // Adjust mode is a live setting now — the gear popover writes it and
  // the renderer, controller and canvas gestures all read the same
  // store, so nothing caches a copy taken at boot.
  const adjustMode = useWheelAdjustMode(wheelAdjustMode)
  const touchUi = adjustMode === 'touch'
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Tearing down an XR session blocks the main thread for a beat, and
  // with no mark on the button the tap reads as having missed
  // (2026-08-24 LUAT: "it feels like the close button was not
  // clicked"). Show a spinner, then let the browser PAINT it before
  // handing over to the work that freezes everything.
  const [closing, setClosing] = useState(false)
  const handleClose = useCallback(() => {
    setClosing((already) => {
      if (already) return already
      afterNextPaint(onClose)
      return true
    })
  }, [onClose])

  const tlStyle = computeContainerStyle('TL', domRotation)
  const trStyle = computeContainerStyle('TR', domRotation)
  const bStyle = computeContainerStyle('B', domRotation, touchUi)
  const b2Style = computeContainerStyle('B2', domRotation, touchUi)
  const isHor = domRotation === 90 || domRotation === 270
  // Glyphs rotate in place ONLY when the pinned-DOM AR session no
  // longer faces the viewer (elRotDeg ≠ 0). Browser mode: DOM auto-
  // rotates with the phone, so --el-rot stays 0deg and this is a
  // no-op (verified on-device: static rotations read wrong there).
  const glyphRotStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'rotate(var(--el-rot, 0deg))',
  }
  // CSS-var carrier merged onto each container so descendants can
  // consume the in-place rotation without prop-drilling.
  const elRotVar = { ['--el-rot' as string]: `${elRotDeg}deg` }
  // ?uiTest — jump straight into the arStarted state so the overlay
  // UI renders without needing WebXR / camera / SLAM. All AR-related
  // gates below short-circuit on this flag so coaching / loading /
  // tap-hint don't fire.
  const [arStarted, setArStarted] = useState(useUiTest)
  const [arStartError, setArStartError] = useState<string | null>(null)
  // The session is starting: Start AR is disabled so a second tap cannot fire.
  const [arStarting, setArStarting] = useState(false)
  const splash = useSyncExternalStore(splashStore.subscribe, splashStore.getState)
  const [configOpen, setConfigOpen] = useState(false)
  const hasModeToggles =
    getTrackMode != null &&
    setTrackMode != null &&
    getPreviewMode != null &&
    setPreviewMode != null

  // Tick every 200ms while coaching is up so the progress bar animates
  // smoothly. sessionInfo is mutated in place on the driver, so we
  // can't subscribe to a store here; a light interval is enough.
  const [convergenceTick, setConvergenceTick] = useState(0)
  useEffect(() => {
    if (getSessionInfo == null) return
    const id = window.setInterval(() => setConvergenceTick((t) => t + 1), 200)
    return () => window.clearInterval(id)
  }, [getSessionInfo])
  const convergence = useMemo(() => {
    if (getSessionInfo == null) return null
    // convergenceTick in deps forces recomputation each interval.
    void convergenceTick
    const info = getSessionInfo()
    return computeTrackingConvergence({
      elapsedMs: info.elapsedMs,
      viewerPoseFrames: info.viewerPoseFrames,
      hitTestFramesWithResult: info.hitTestFramesWithResult,
      hitTestFramesEmpty: info.hitTestFramesEmpty,
    })
  }, [getSessionInfo, convergenceTick])

  // XR pose can't tell us physical rotation on Chrome Android — the
  // WebXR runtime normalizes the viewer pose so local +Y always aligns
  // with world +Y (the "user's up" as displayed), not with the phone
  // chassis top. Meaning the pose reports "portrait" whether the phone
  // is landscape or portrait, so no useful signal. Landscape state
  // stays derived from the DOM signal, polled below.

  const hasPlacedWheel = controllerState.placedWheelCount > 0
  const hasModel = loaderState.hasLoadedModel
  const isTrackingReady =
    sessionState.kind === 'ready' ||
    sessionState.kind === 'tracking' ||
    sessionState.kind === 'placing' ||
    sessionState.kind === 'placed'
  const isTrackingLimited = sessionState.kind === 'limited'

  // Overlay priority (top of stack hides everything below):
  // 1. StartAR overlay until user grants camera
  // 2. Coaching overlay while SLAM is initialising/scanning OR has gone
  //    limited after ready — blocks the reticle so placement waits
  // 3. Loading hint while assembler is polling / GLB downloading
  // 4. Reticle + aim hint whenever the AIMING flow is live (first wheel
  //    OR "+"-re-armed) — tracking ready + model ready + renderer armed
  const showCoachingOverlay =
    !useUiTest &&
    !hideWheelUI &&
    arStarted &&
    (!isTrackingReady || isTrackingLimited)
  // The loader has always recorded WHY a load stopped; nothing rendered
  // it, so a failed load was indistinguishable from a slow one and the
  // "Loading wheel…" card stayed up for ever (2026-08-24 LUAT).
  const loadError = loadFailureMessage(loaderState)
  const showLoadingHint =
    !useUiTest &&
    !hideWheelUI &&
    arStarted &&
    isTrackingReady &&
    !hasModel &&
    loadError == null
  const showLoadError =
    !useUiTest && !hideWheelUI && arStarted && !hasModel && loadError != null
  const showTapHint =
    !useUiTest &&
    !hideWheelUI &&
    arStarted &&
    isTrackingReady &&
    hasModel &&
    // Aiming-capable renderers (depth flow) drive the hint from the
    // aiming state so it returns for every "+" placement; legacy
    // renderers keep the first-wheel-only behaviour.
    (renderer.isAiming?.() ?? !hasPlacedWheel)

  const dismissToast = useCallback(() => toastStore.dismiss(), [toastStore])

  const startingRef = useRef(false)
  const handleStart = useCallback(async () => {
    if (startingRef.current) return
    startingRef.current = true
    setArStarting(true)
    setArStartError(null)
    try {
      await onStartAR()
      setArStarted(true)
    } catch (e) {
      // The device that cannot start AR at all reports nothing else —
      // no session means no session events — so this catch is the only
      // place its failure is observable (2026-08-24).
      atoLog.error('ar_session_start', e)
      setArStartError(e instanceof Error ? e.message : String(e))
    } finally {
      startingRef.current = false
      setArStarting(false)
    }
  }, [onStartAR])

  const wheelInfo = useMemo(
    () =>
      projectTopControlsWheelInfo(
        loaderState,
        controllerState.adjustmentsForSelected.scaleMultiplier,
      ),
    [loaderState, controllerState.adjustmentsForSelected.scaleMultiplier],
  )

  // The splash's brand: by wheel id from the parts API, else (a launch URL
  // without one) the assembly job's brand once its logo is known — never the
  // job's "AutoViz" stand-in name.
  const jobLogo = wheelInfo?.brandLogoURL ?? null
  const jobBrandName = wheelInfo?.brandName ?? ''
  const splashBrand = useMemo<SplashBrand | null>(() => {
    if (splash.brand) return splash.brand
    if (!jobLogo) return null
    try {
      return { name: jobBrandName, logoURL: new URL(jobLogo) }
    } catch {
      return null
    }
  }, [splash.brand, jobLogo, jobBrandName])

  const pillBrand = useUiTest ? 'Azad' : wheelInfo?.brandName ?? ''
  const pillModel = useUiTest ? 'AZ99' : wheelInfo?.modelName ?? ''
  const shouldShowPill = pillBrand.length > 0 && pillModel.length > 0

  // Shared by both overlay trees (?use_ios_ui keeps the splash and the
  // scanning overlay: WebXR needs the gesture, and has to find planes).
  const splashElement = !arStarted && (
    splash.mode != null ? (
      <ArSplash
        mode={splash.mode}
        brand={splashBrand}
        starting={arStarting}
        error={arStartError}
        onStart={handleStart}
      />
    ) : (
      // The boot probe has not answered yet: the splash's ground, no rows.
      <div data-testid="ar-splash-pending" style={{ position: 'fixed', inset: 0, background: '#f5f5f5' }} />
    )
  )
  const coachingElement = showCoachingOverlay && (
    <CoachingOverlay
      // The headline is an INSTRUCTION, not a status. "Scanning
      // surface" described what the app was doing and left the
      // tester holding the phone still (2026-08-22 field report);
      // depth only converges while the phone is translating.
      title={
        isTrackingLimited
          ? 'Move your phone to rescan'
          : 'Move your phone to scan'
      }
      subtitle={
        isTrackingLimited
          ? 'Swing it slowly toward and away from your car'
          : 'Swing it toward and away from your car until the target appears'
      }
      progress={
        !isTrackingLimited && convergence != null
          ? convergence.progress
          : undefined
      }
      hint={
        !isTrackingLimited && convergence != null
          ? convergence.hint
          : undefined
      }
      placeAnywayUnlocked={
        !isTrackingLimited && convergence?.placeAnywayUnlocked === true
      }
      onPlaceAnyway={onForceReady}
    />
  )

  if (useIosUi && iosController != null) {
    const exitTo = (success: boolean) => {
      if (exit == null) return
      navigate(
        exit.isReturnURL
          ? buildArCompleteUrl({ returnURL: exit.url, success, wheels: controllerState.placedWheelCount })
          : exit.url.toString(),
      )
    }
    return (
      <>
        {splashElement}
        {coachingElement}
        {arStarted && iosState != null && (
          <IosUiOverlay
            loader={loaderState}
            placement={controllerState}
            ios={iosState}
            isTrackingGood={isTrackingReady && !isTrackingLimited}
            isLandscape={isLandscape}
            viewSize={viewportSize}
            hasReturnURL={exit?.isReturnURL === true}
            exitIsDeadEndCapable={exit != null}
            closing={closing}
            toastMessage={toastMessage}
            onToastDismiss={dismissToast}
            version={__APP_VERSION__}
            maxCamberRad={MAX_CAMBER_ANGLE_RAD}
            heightStep={MANUAL_ADJUSTMENT_STEPS.height}
            horizontalStep={MANUAL_ADJUSTMENT_STEPS.horizontal}
            getRenderedQuadrant={getRenderedQuadrant}
            actions={{
              placeFramed: () =>
                iosController.placeFramed({
                  screenPoint: { x: viewportSize.width / 2, y: viewportSize.height / 2 },
                  viewSize: viewportSize,
                  isLandscape,
                }),
              cancelPlaceUI: () => iosController.cancelPlaceUI(),
              openPlaceUI: () => iosController.openPlaceUI(),
              setCurrentWheelSize: (inches) => iosController.setCurrentWheelSize(inches),
              dismissSizeQuestion: () => iosController.dismissCurrentWheelSizeQuestion(),
              setSelectedWheelSize: (inches) => iosController.setSelectedWheelSize(inches),
              deleteSelected: () => iosController.deleteSelectedWheel(),
              adjustHeight: (d) => controller.adjustHeight(d),
              adjustHorizontal: (d) => controller.adjustHorizontal(d),
              adjustDepth: (d) => controller.adjustDepth(d),
              adjustTilt: (d) => controller.adjustTilt(d),
              adjustYaw: (d) => controller.adjustYaw(d),
              retryLoad: () => onRetryLoad?.(),
              cancelLoad: () => {
                onCancelLoad?.()
                exitTo(false)
              },
              exit: exitTo,
              close: () => (exit?.isReturnURL === true ? exitTo(true) : handleClose()),
              camera: () => onCamera?.(),
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      {/* Three fixed-positioned chassis-anchored containers replace
          the old full-page wrapper. Each computes its DOM position
          from `domRotation` so it always lands at its chassis corner.
          For non-v2 (useControlChips=false) we fall back to the old
          TopControlsView flex row, since that path isn't the ATO
          target and doesn't need the split. */}
      {useControlChips ? (
        <>
          {/* TL container — model-name pill */}
          {shouldShowPill && (
            <div data-testid="container-tl" style={{ ...tlStyle, ...elRotVar }}>
              <button
                type="button"
                onClick={() => undefined}
                data-testid="wheel-info-pill"
                style={{
                  // Bottom margin matches left so the pill keeps equal
                  // spacing when the TL container anchors to the
                  // bottom-left corner in landscape.
                  margin: '33px 5px 5px 5px',
                  background: 'rgba(0, 0, 0, 0.75)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  textShadow: '0 0 3px #000',
                  // In-place rotation for pinned-DOM AR sessions —
                  // no-op (0deg) in browser mode.
                  transform: 'rotate(var(--el-rot, 0deg))',
                }}
              >
                {pillBrand} {pillModel}
              </button>
            </div>
          )}
          {/* TR container — Close / Refresh / Camera */}
          <div data-testid="container-tr" style={{ ...trStyle, ...elRotVar }}>
            <div
              style={{
                // Portrait keeps the 33px status-bar offset; landscape
                // has no status bar on this edge, so top spacing
                // matches the left spacing for an even corner inset.
                margin: isHor ? '10px 10px 0 10px' : '33px 10px 0 5px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <CircleIconButton
                ariaLabel="Close"
                onClick={handleClose}
                busy={closing}
              >
                <span style={glyphRotStyle}>
                  <X size={20} strokeWidth={2} />
                </span>
              </CircleIconButton>
              <CircleIconButton
                ariaLabel="Settings"
                onClick={() => setSettingsOpen((open) => !open)}
              >
                <span style={glyphRotStyle}>
                  <Settings size={20} strokeWidth={2} />
                </span>
              </CircleIconButton>
              <CircleIconButton
                ariaLabel="Enter Camera Mode"
                onClick={() => undefined}
              >
                <span style={glyphRotStyle}>
                  <Camera size={20} strokeWidth={2} />
                </span>
              </CircleIconButton>
            </div>
          </div>
        </>
      ) : (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            pointerEvents: 'auto',
          }}
        >
          <TopControlsView
            showDoneButton={false}
            wheelInfo={wheelInfo}
            closeTitle="Close"
            onClose={handleClose}
            closing={closing}
            onCamera={() => onCamera?.()}
            onSettings={() => setSettingsOpen((open) => !open)}
            v2Layout={false}
            onDone={() => undefined}
            onWheelInfoTap={() => undefined}
            onSelectSize={() => undefined}
            isLandscape={isLandscape}
          />
        </div>
      )}

      {splashElement}

      {coachingElement}

      {showLoadingHint && (
        <InstructionOverlay message="Loading wheel…" isLoading />
      )}

      {/* No spinner: this is where it STOPPED. Close is the only way
          on from here — nothing in the app re-runs a load today, and
          offering a Retry that silently does nothing would be worse
          than offering none. */}
      {showLoadError && (
        <InstructionOverlay
          message={loadError}
          action={
            <button
              type="button"
              data-testid="load-error-close"
              onClick={handleClose}
              style={{
                boxSizing: 'border-box',
                background: 'rgba(255,255,255,0.14)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.5)',
                borderRadius: 999,
                padding: '10px 22px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          }
        />
      )}

      {/* The camera-locked preview wheel (rendered by the AR scene) is
          the visual aim affordance; we just keep a short text hint. */}
      {showTapHint && (
        <InstructionOverlay
          wide
          message={
            <>
              Align the{' '}
              <span style={{ color: GAUGE_LINE_COLOR_HEX, fontWeight: 700 }}>
                green line
              </span>{' '}
              to where
              <br />
              your car&#39;s tire touches the ground.
            </>
          }
          action="TAP TO PLACE"
        />
      )}

      {/* ?groundLine=1 — screen-locked reference stripe at ~65% screen Y.
          Operator aligns it with the tire-ground contact; first tap
          raycasts the driver's downward-angled hit-test source at that
          direction to derive the fixed ground Y for every subsequent
          placement. Pointer-events off so it never eats a tap. */}
      {useGroundLine && arStarted && (
        <div
          data-testid="ground-line-overlay"
          style={{
            position: 'fixed',
            top: '65%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            maxWidth: 400,
            height: 2,
            background: 'rgba(0, 150, 255, 0.9)',
            boxShadow: '0 0 8px rgba(0, 150, 255, 0.55)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      )}

      <Toast message={toastMessage} onDismiss={dismissToast} />

      {arStarted && (
        <TrackingLed good={isTrackingReady && !isTrackingLimited} />
      )}

      <VersionBadge />

      {/* Orientation debug HUD (?orHud) — shows BOTH signals side by
          side. `D:L/P` = DOM signal (screen.orientation / matchMedia).
          `X:L/P/?` = DeviceOrientationEvent (accelerometer roll). "?"
          means DOE hasn't fired yet. Amber = the two disagree. R: =
          screen.orientation.angle. */}
      {showOrientationHud && (
      <div
        data-testid="landscape-debug-badge"
        style={{
          position: 'fixed',
          right: 4,
          bottom: 4,
          padding: '2px 6px',
          fontSize: 10,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          background:
            doeIsLandscape != null && doeIsLandscape !== isLandscape
              ? 'rgba(220, 140, 0, 0.9)'
              : isLandscape
                ? 'rgba(0, 180, 60, 0.85)'
                : 'rgba(80, 80, 80, 0.75)',
          color: 'white',
          borderRadius: 3,
          pointerEvents: 'none',
          zIndex: 20,
        }}
      >
        D:{isLandscape ? 'L' : 'P'} X:
        {doeIsLandscape == null ? '?' : doeIsLandscape ? 'L' : 'P'} R:
        {domRotation} E:{elRotDeg}
      </div>
      )}

      {diagnosticsStore != null && arStarted && (
        <DiagnosticsHUD store={diagnosticsStore} />
      )}

      {/* B container — control bar. Contains the floating icon row
          (WheelSelector + gear + delete) and the manual-adjustment
          panel stacked. In portrait, it's a full-width strip at the
          bottom of the DOM. In landscape, its bStyle turns it into a
          narrow full-height strip on the chassis-bottom side. */}
      {/* B2 container — the wheels-nav + gear + delete strip.
          ONE container used for every orientation. Its position + flex
          direction are computed in computeContainerStyle('B2', angle):
          portrait/reverse = horizontal row above/below B, landscape =
          vertical column beside B. No container-level rotate; only
          layout coordinates change. */}
      {arStarted &&
        (useUiTest || controllerState.selectedWheelId != null) &&
        useControlChips && (
          <div data-testid="container-b2" style={{ ...b2Style, ...elRotVar }}>
            <WheelSelector
              wheelIds={
                useUiTest
                  ? (['ui-test-wheel-1'] as const)
                  : controllerState.placedWheelIds
              }
              selectedId={
                useUiTest
                  ? 'ui-test-wheel-1'
                  : controllerState.selectedWheelId
              }
              maxWheels={MAX_WHEELS}
              onSelect={(id) => controller.selectWheel(id)}
              onAdd={() =>
                controller.addWheelAtCameraTarget({
                  screenPoint: { x: 0, y: 0 },
                  viewSize: {
                    width: window.innerWidth,
                    height: window.innerHeight,
                  },
                  isLandscape,
                })
              }
              size={39}
              inline
              vertical={isHor}
              reversed={domRotation === 90}
              isLandscape={isLandscape}
            />
            <div
              style={{
                display: 'flex',
                // Same chassis-riding order rule as the outer B2:
                // angle 90 reverses so bottom-to-top = [gear][delete].
                flexDirection: isHor
                  ? domRotation === 90
                    ? 'column-reverse'
                    : 'column'
                  : 'row',
                gap: 8,
                alignItems: 'center',
                position: 'relative',
              }}
            >
              {hasModeToggles && (
                <CircleIconButton
                  ariaLabel="Debug config"
                  onClick={() => setConfigOpen((o) => !o)}
                >
                  <span style={glyphRotStyle}>
                    <Settings size={20} strokeWidth={2} />
                  </span>
                </CircleIconButton>
              )}
              <CircleIconButton
                ariaLabel="Delete wheel"
                onClick={() => controller.deleteSelectedWheel()}
                color="rgba(255, 80, 80, 0.9)"
              >
                <span style={glyphRotStyle}>
                  <Trash2 size={20} strokeWidth={2} />
                </span>
              </CircleIconButton>
              {hasModeToggles && configOpen && (
                <DebugConfigPopover
                  getTrackMode={getTrackMode}
                  setTrackMode={setTrackMode}
                  getPreviewMode={getPreviewMode}
                  setPreviewMode={setPreviewMode}
                />
              )}
            </div>
          </div>
        )}

      {settingsOpen && wheelAdjustMode != null && (
        <SettingsPopover
          mode={adjustMode}
          onSelect={(next) => wheelAdjustMode.set(next)}
          onDismiss={() => setSettingsOpen(false)}
        />
      )}

      {arStarted &&
        // Under ?touchUi the wheel nav / trash row is furniture: fixed to
        // the bottom, always present. Tying it to selection made it
        // appear and vanish as the user touched wheels, which is exactly
        // the flicker the 2026-08-23 LUAT called out.
        (useUiTest || touchUi || controllerState.selectedWheelId != null) && (
        <div data-testid="container-b" style={bStyle}>
          {(() => {
            // ?uiTest — swap in fake wheelId + placed list so the panel
            // renders without a real placement.
            // With touchUi the panel outlives selection, so a null id is
            // a real state — the selector shows no chip highlighted and
            // delete has nothing to act on. Outside touchUi the panel
            // only renders WITH a selection, so the fallback stands.
            const wheelId = useUiTest
              ? 'ui-test-wheel-1'
              : touchUi
                ? controllerState.selectedWheelId
                : (controllerState.selectedWheelId ?? 'ui-test-wheel-1')
            const placedWheelIds = useUiTest
              ? (['ui-test-wheel-1'] as const)
              : controllerState.placedWheelIds
            return useControlChips && !touchUi ? (
              <ManualAdjustmentPanelV2
                wheelId={wheelId ?? 'ui-test-wheel-1'}
                isLandscape={isLandscape}
                rotateInPlaceDeg={elRotDeg}
                onHeightChange={(d) => controller.adjustHeight(d)}
                onDepthChange={(d) => controller.adjustDepth(d)}
                onHorizontalChange={(d) => controller.adjustHorizontal(d)}
                onTiltChange={(d) => controller.adjustTilt(d)}
                onYawChange={(d) => controller.adjustYaw(d)}
                onScaleChange={(d) => controller.adjustScale(d)}
                onDelete={() => controller.deleteSelectedWheel()}
                onCamera={() => undefined}
                currentTiltAngle={
                  controllerState.adjustmentsForSelected.tiltAngle
                }
                currentYawAngle={
                  controllerState.adjustmentsForSelected.yawAngle
                }
                currentDepth={
                  controllerState.adjustmentsForSelected.depthOffset
                }
                currentScale={
                  controllerState.adjustmentsForSelected.scaleMultiplier
                }
                originalDiameterInches={
                  loaderState.wheelInfo.assembledSize?.diameterInches ??
                  loaderState.wheelInfo.selectedSize?.diameterInches ??
                  loaderState.glbDiameterInches ??
                  26
                }
                placedWheelIds={placedWheelIds}
                maxWheels={MAX_WHEELS}
                onSelectWheel={(id) => controller.selectWheel(id)}
                onAddWheel={() =>
                  controller.addWheelAtCameraTarget({
                    screenPoint: { x: 0, y: 0 },
                    viewSize: {
                      width: window.innerWidth,
                      height: window.innerHeight,
                    },
                    isLandscape,
                  })
                }
              />
            ) : (
              <ManualAdjustmentPanel
                wheelId={wheelId}
                isLandscape={isLandscape}
                touchUi={touchUi}
                placementPending={controllerState.placementPending}
                onHeightChange={(d) => controller.adjustHeight(d)}
                onDepthChange={(d) => controller.adjustDepth(d)}
                onHorizontalChange={(d) => controller.adjustHorizontal(d)}
                onTiltChange={(d) => controller.adjustTilt(d)}
                onYawChange={(d) => controller.adjustYaw(d)}
                onScaleChange={(d) => controller.adjustScale(d)}
                onDelete={() => controller.deleteSelectedWheel()}
                currentTiltAngle={
                  controllerState.adjustmentsForSelected.tiltAngle
                }
                currentYawAngle={
                  controllerState.adjustmentsForSelected.yawAngle
                }
                currentDepth={
                  controllerState.adjustmentsForSelected.depthOffset
                }
                currentScale={
                  controllerState.adjustmentsForSelected.scaleMultiplier
                }
                originalDiameterInches={
                  loaderState.wheelInfo.assembledSize?.diameterInches ??
                  loaderState.wheelInfo.selectedSize?.diameterInches ??
                  loaderState.glbDiameterInches ??
                  26
                }
                placedWheelIds={placedWheelIds}
                maxWheels={MAX_WHEELS}
                onSelectWheel={(id) => controller.selectWheel(id)}
                onAddWheel={() =>
                  controller.addWheelAtCameraTarget({
                    screenPoint: { x: 0, y: 0 },
                    viewSize: {
                      width: window.innerWidth,
                      height: window.innerHeight,
                    },
                    isLandscape,
                  })
                }
              />
            )
          })()}
        </div>
      )}
    </>
  )
}

// ── hooks ──────────────────────────────────────────────────────────────

function useExternalStore(loader: WheelLoader): WheelLoaderState {
  const subscribe = useMemo(
    () => (cb: () => void) => loader.subscribe(() => cb()),
    [loader],
  )
  const getSnapshot = useCallback(() => loader.getState(), [loader])
  return useSyncExternalStore(subscribe, getSnapshot)
}

/** Live adjust mode. Falls back to touch — the default — when no store
 *  is supplied (tests and the ?uiTest harness mount App bare). */
function useWheelAdjustMode(store?: WheelAdjustModeStore) {
  return useSyncExternalStore(
    (listener) => store?.subscribe(listener) ?? (() => undefined),
    () => store?.get() ?? 'touch',
    () => 'touch' as const,
  )
}

function useControllerStore(
  controller: WheelPlacementController,
): WheelPlacementControllerState {
  const subscribe = useMemo(
    () => (cb: () => void) => controller.subscribe(() => cb()),
    [controller],
  )
  const getSnapshot = useCallback(() => controller.getState(), [controller])
  return useSyncExternalStore(subscribe, getSnapshot)
}

function useSessionState(renderer: WheelRenderer): ARSessionState {
  const subscribe = useMemo(
    () => (cb: () => void) => renderer.subscribeSessionState(() => cb()),
    [renderer],
  )
  const getSnapshot = useCallback(
    () => renderer.getSessionState(),
    [renderer],
  )
  return useSyncExternalStore(subscribe, getSnapshot)
}

/** ?use_ios_ui — the iOS controller's state; null without one. */
function useIosPlacementState(controller?: IosPlacementController): IosPlacementState | null {
  return useSyncExternalStore(
    (listener) => controller?.subscribe(listener) ?? (() => undefined),
    () => controller?.getState() ?? null,
    () => null,
  )
}

/** ?use_ios_ui — the viewport the framing ring is laid out in. Same
 *  belt-and-braces as useIsLandscape (events plus a 300 ms poll), and
 *  inert unless enabled so the shipped tree gains no timer. */
function useViewportSize(enabled: boolean): { width: number; height: number } {
  const [size, setSize] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }))
  useEffect(() => {
    if (!enabled) return undefined
    const handler = () =>
      setSize((prev) =>
        prev.width === window.innerWidth && prev.height === window.innerHeight
          ? prev
          : { width: window.innerWidth, height: window.innerHeight },
      )
    window.addEventListener('resize', handler)
    const pollId = window.setInterval(handler, 300)
    return () => {
      window.clearInterval(pollId)
      window.removeEventListener('resize', handler)
    }
  }, [enabled])
  return size
}

function useToastStore(toastStore: ToastStoreLike): string | null {
  const subscribe = useMemo(
    () => (cb: () => void) => toastStore.subscribe(() => cb()),
    [toastStore],
  )
  const getSnapshot = useCallback(() => toastStore.getState(), [toastStore])
  return useSyncExternalStore(subscribe, getSnapshot)
}

/** DOM-driven landscape signal that works BOTH pre-launch and during
 *  an active WebXR immersive-ar session. Chrome/Android is unreliable
 *  about firing `resize` / `orientationchange` / `screen.orientation
 *  .change` / matchMedia change events during rotation — especially
 *  once immersive-ar is active. So we listen on every available event
 *  (whichever fires first updates state) AND poll every 300 ms as a
 *  backstop: even if all events swallow the rotation, the poll picks
 *  up the state change within one tick. */
function useIsLandscape(): boolean {
  const [isLandscape, setIsLandscape] = useState(() => detectDomLandscape())
  useEffect(() => {
    const handler = () => setIsLandscape(detectDomLandscape())
    window.addEventListener('resize', handler)
    window.addEventListener('orientationchange', handler)
    const mql = window.matchMedia?.('(orientation: landscape)')
    if (mql?.addEventListener != null) mql.addEventListener('change', handler)
    else mql?.addListener?.(handler)
    const so = (screen as unknown as {
      orientation?: {
        addEventListener?: (e: string, cb: () => void) => void
        removeEventListener?: (e: string, cb: () => void) => void
      }
    }).orientation
    so?.addEventListener?.('change', handler)
    // Backstop poll — every 300 ms, re-evaluate the DOM signal.
    // React de-dupes identical setState calls so this is cheap when
    // orientation isn't changing.
    const pollId = window.setInterval(handler, 300)
    return () => {
      window.clearInterval(pollId)
      window.removeEventListener('resize', handler)
      window.removeEventListener('orientationchange', handler)
      if (mql?.removeEventListener != null)
        mql.removeEventListener('change', handler)
      else mql?.removeListener?.(handler)
      so?.removeEventListener?.('change', handler)
    }
  }, [])
  return isLandscape
}

/** Live-tracked DOM rotation angle from canonical portrait (in degrees,
 *  CW positive). Reads `screen.orientation.angle` — 0 = portrait,
 *  90 = landscape-primary (device rotated CCW so the physical top edge
 *  faces the user's left), 180 = reverse portrait, 270 = the other
 *  landscape. Used by the overlay wrapper to counteract Chrome's DOM
 *  auto-rotation and keep the portrait-canonical layout anchored to
 *  the phone chassis. Polled at 300 ms to catch changes when events
 *  don't fire. */
function useDomRotation(): number {
  const [rotation, setRotation] = useState<number>(() => readDomAngle())
  useEffect(() => {
    const handler = () => setRotation(readDomAngle())
    window.addEventListener('resize', handler)
    window.addEventListener('orientationchange', handler)
    const so = (screen as unknown as {
      orientation?: {
        addEventListener?: (e: string, cb: () => void) => void
        removeEventListener?: (e: string, cb: () => void) => void
      }
    }).orientation
    so?.addEventListener?.('change', handler)
    const pollId = window.setInterval(handler, 300)
    return () => {
      window.clearInterval(pollId)
      window.removeEventListener('resize', handler)
      window.removeEventListener('orientationchange', handler)
      so?.removeEventListener?.('change', handler)
    }
  }, [])
  return rotation
}

/** Physical-rotation signal from `DeviceOrientationEvent` — a sensor
 *  stream on `window` that keeps firing during a WebXR immersive-ar
 *  session (unlike `resize` / `orientationchange` / matchMedia, which
 *  Chrome suppresses while the overlay is pinned). `event.gamma` is
 *  roll around the phone's long axis in degrees, range -90 to +90;
 *  |gamma| > 45 is the standard landscape threshold. Hysteresis
 *  (enter at 50°, exit at 40°) prevents the boundary from flapping
 *  when the operator holds the phone near-diagonal.
 *
 *  Returns `null` until the first event arrives (some browsers require
 *  a permission prompt on iOS; on Chrome Android events start
 *  immediately). Consumers should treat null as "unknown, use DOM
 *  signal as fallback."
 *
 *  Currently used for DIAGNOSTIC LOGGING ONLY — no UI response is
 *  wired to this yet. Once we confirm it fires consistently both in
 *  browser and during AR, the compensation-rotation logic keys off it. */
function useDevicePhysicalRotation(): number | null {
  const [state, setState] = useState<number | null>(null)
  const stateRef = useRef<number | null>(null)
  useEffect(() => {
    const ENTER_LANDSCAPE_DEG = 50
    const EXIT_LANDSCAPE_DEG = 40
    const handler = (e: DeviceOrientationEvent) => {
      const gamma = e.gamma
      if (gamma == null) return
      const abs = Math.abs(gamma)
      let next = stateRef.current
      if (abs > ENTER_LANDSCAPE_DEG) {
        // Signed gamma distinguishes the two landscape directions.
        // Mapping to screen.orientation.angle semantics verified via
        // the [ATO] physRotation log during UAT — flip the ternary if
        // a device family reports mirrored.
        next = gamma > 0 ? 90 : 270
      } else if (abs < EXIT_LANDSCAPE_DEG) {
        next = 0
      }
      // else: within the hysteresis dead-zone → keep previous
      if (next !== stateRef.current) {
        stateRef.current = next
        setState(next)
        // eslint-disable-next-line no-console
        console.log('[ATO] physRotation:', next, { gamma: gamma.toFixed(1) })
      }
    }
    window.addEventListener('deviceorientation', handler)
    return () => window.removeEventListener('deviceorientation', handler)
  }, [])
  return state
}

/** Landscape detection prefers `screen.orientation.type` (unambiguous,
 *  reports the physical device orientation), falls through to the CSS
 *  `(orientation: landscape)` media query, and finally to a raw
 *  window dimension comparison. Order matches "reliable → naive." */
function detectDomLandscape(): boolean {
  const type = (
    screen as unknown as { orientation?: { type?: string } }
  ).orientation?.type
  if (typeof type === 'string' && type.length > 0) {
    return type.startsWith('landscape')
  }
  const mql = window.matchMedia?.('(orientation: landscape)')
  if (mql?.matches != null) return mql.matches
  return window.innerWidth > window.innerHeight
}

/** Per-container CSS for the three chassis-anchored surfaces (TL, TR,
 *  B) based on the DOM's current rotation angle. Each container is
 *  `position: fixed` at its designated chassis corner. Chrome auto-
 *  rotates the DOM to match phone rotation, so DOM coordinates align
 *  with the user's view — no container-level rotation transforms are
 *  needed for text readability. We just translate positioning by
 *  orientation.
 *
 *  Chassis-to-DOM-corner mapping:
 *    angle=0  (portrait):        DOM top-left = chassis TL_p
 *    angle=90 (CCW-landscape):   DOM top-left = chassis TR_p
 *    angle=270 (CW-landscape):   DOM top-left = chassis BL_p
 *    angle=180 (reverse portrait): DOM top-left = chassis BR_p
 *
 *  From these, place each chassis corner at the appropriate DOM
 *  corner. The B (control bar) container becomes a narrow vertical
 *  strip on the physical chassis-bottom side for landscape holds. */
function computeContainerStyle(
  corner: 'TL' | 'TR' | 'B' | 'B2',
  angle: number,
  /** ?touchUi — the control bar loses its 180px black slab and shrinks
   *  to the buttons themselves. With the joystick gone the slab was
   *  reserving a third of the screen for two chips and a trash icon,
   *  over the very AR view the user now has to reach into
   *  (2026-08-23 LUAT). */
  bare = false,
): CSSProperties {
  const B_THICKNESS = 180
  // Clearance for the bare bar, as a LITERAL — env() is useless here.
  //
  // The AR session runs as a WebXR dom-overlay (driver: domOverlay root
  // = document.body). Chrome renders that fullscreen and reports
  // env(safe-area-inset-bottom) as 0, while Android's gesture bar still
  // covers the bottom ~48px. So two rounds of "add safe-area padding"
  // added nothing at all and the buttons stayed under the gesture bar
  // (2026-08-23 LUAT, twice). The inset is kept for devices that do
  // report one, but the literal is what actually clears the bar.
  // Raised 43 → 53 on 2026-08-24 LUAT ("move the entire wheel nav ui
  // elements up 10px"): the row cleared the gesture bar but still sat
  // tight against it.
  const BARE_BAR_BOTTOM_CLEARANCE_PX = 53
  // Landscape strip is slightly wider than the portrait bar is tall —
  // the chip labels need the extra room at the narrower aspect.
  const B_WIDTH_HOR = 200
  const B_PAD = 8
  const base = { position: 'fixed' as const, pointerEvents: 'auto' as const }
  if (corner === 'TL') {
    switch (angle) {
      case 90:
        return { ...base, bottom: 0, left: 0 }
      case 270:
        return { ...base, top: 0, right: 0 }
      case 180:
        return { ...base, bottom: 0, right: 0 }
      default:
        return { ...base, top: 0, left: 0 }
    }
  }
  if (corner === 'TR') {
    switch (angle) {
      case 90:
        return { ...base, top: 0, left: 0 }
      case 270:
        return { ...base, bottom: 0, right: 0 }
      case 180:
        return { ...base, bottom: 0, left: 0 }
      default:
        return { ...base, top: 0, right: 0 }
    }
  }
  // B (control bar). Portrait = full-width bottom strip. Landscape =
  // narrow 180-px vertical strip anchored to the physical chassis-
  // bottom side (DOM right for angle 90, DOM left for angle 270),
  // full viewport height. Uses 100dvh so it respects the on-screen
  // toolbar's dynamic viewport, unlike 100vh. Background applied so
  // the container's occupied area is visible even where the inner
  // panel content hasn't grown to fill it.
  if (corner === 'B2') {
    // Wheels-nav (B2.1) + config/delete (B2.2). In portrait it hovers
    // as a horizontal row ABOVE the B panel, space-between so the two
    // groups sit at opposite ends. In landscape it becomes a vertical
    // column beside B, spanning the full height, space-between again.
    // Angle 90 uses column-REVERSE: the strip behaves as if it rode
    // along with the chassis rotation, so portrait's left-to-right
    // ([1][+] … [gear][delete]) maps to bottom-to-top — first DOM
    // child lands at the bottom. Angle 270 mirrors with normal column.
    switch (angle) {
      case 90:
        return {
          ...base,
          top: B_PAD,
          bottom: B_PAD,
          right: B_WIDTH_HOR + B_PAD,
          display: 'flex',
          flexDirection: 'column-reverse',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 5,
        }
      case 270:
        return {
          ...base,
          top: B_PAD,
          bottom: B_PAD,
          left: B_WIDTH_HOR + B_PAD,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 5,
        }
      case 180:
        return {
          ...base,
          top: B_THICKNESS + B_PAD,
          left: 15,
          right: 15,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 5,
        }
      default:
        return {
          ...base,
          bottom: B_THICKNESS + B_PAD,
          left: 15,
          right: 15,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 5,
        }
    }
  }
  const bBackground = bare ? 'transparent' : 'rgba(0, 0, 0, 0.85)'
  // A bare bar hugs the screen edge, so it needs the inset the 180px
  // slab used to provide: without it the buttons sit under the system
  // gesture bar and are clipped off-screen (2026-08-23 LUAT).
  //
  // The clearance is measured against the version badge rather than
  // guessed: the badge is fixed at bottom:8 with ~6px text, so it owns
  // roughly the bottom 16px. Clearing it by a further 12px puts the
  // buttons above the badge with a comfortable margin off the edge,
  // which is the placement the dev asked for.
  //
  // It also stops swallowing taps. At full width with no visible
  // background, an auto-pointer container would silently eat every tap
  // near the bottom of the AR view — the region the user now reaches
  // into. The bar passes taps through and the panel inside re-enables
  // pointers for its own buttons.
  const bareInsets: CSSProperties = bare
    ? {
        pointerEvents: 'none',
        paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${BARE_BAR_BOTTOM_CLEARANCE_PX}px)`,
        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 12px)',
        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 12px)',
      }
    : {}
  switch (angle) {
    case 90:
      return {
        ...base,
        top: 0,
        bottom: 0,
        right: 0,
        ...(bare ? {} : { width: B_WIDTH_HOR }),
        ...bareInsets,
        background: bBackground,
      }
    case 270:
      return {
        ...base,
        top: 0,
        bottom: 0,
        left: 0,
        ...(bare ? {} : { width: B_WIDTH_HOR }),
        ...bareInsets,
        background: bBackground,
      }
    case 180:
      return {
        ...base,
        top: 0,
        left: 0,
        right: 0,
        ...(bare ? {} : { height: B_THICKNESS }),
        ...bareInsets,
        background: bBackground,
      }
    default:
      return {
        ...base,
        bottom: 0,
        left: 0,
        right: 0,
        ...(bare ? {} : { height: B_THICKNESS }),
        ...bareInsets,
        background: bBackground,
      }
  }
}

/** Bottom-left LED — 4 px circle. Green when session state is in the
 *  ready/tracking family AND not limited (same signal that dismisses
 *  the coaching overlay). Red on any degradation. Always visible
 *  while AR is running so the operator can see at a glance whether
 *  ARCore currently has good tracking. */
function TrackingLed({ good }: { good: boolean }) {
  return (
    <div
      data-testid="tracking-led"
      data-good={good ? '1' : '0'}
      style={{
        position: 'fixed',
        // Derived from the badge's anchor so the two move together —
        // they used to carry independent coordinates, which is why the
        // LED stayed put when the badge moved (2026-08-23 LUAT).
        right: BUILD_STAMP_ANCHOR.right - BUILD_STAMP_ANCHOR.ledGapPx,
        bottom: BUILD_STAMP_ANCHOR.bottom + BUILD_STAMP_ANCHOR.ledRisePx,
        width: 4,
        height: 4,
        borderRadius: '50%',
        background: good ? '#3ec45b' : '#e05252',
        boxShadow: good
          ? '0 0 4px rgba(62,196,91,0.85)'
          : '0 0 4px rgba(224,82,82,0.85)',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    />
  )
}

interface DebugConfigPopoverProps {
  getTrackMode: () => 'plane' | 'world'
  setTrackMode: (m: 'plane' | 'world') => void
  getPreviewMode: () => 'camera-locked' | 'plane-anchored'
  setPreviewMode: (m: 'camera-locked' | 'plane-anchored') => void
}

/** Popover anchored to the gear button in the v2 floating row.
 *  Consolidates the PREV / PLANE cycle pills that used to live in
 *  separate corner overlays (mounted by main.ts under ?trackToggle). */
function DebugConfigPopover({
  getTrackMode,
  setTrackMode,
  getPreviewMode,
  setPreviewMode,
}: DebugConfigPopoverProps) {
  const [trackMode, setLocalTrackMode] = useState(getTrackMode())
  const [previewMode, setLocalPreviewMode] = useState(getPreviewMode())
  const cycleTrack = useCallback(() => {
    const next: 'plane' | 'world' = trackMode === 'plane' ? 'world' : 'plane'
    setLocalTrackMode(next)
    setTrackMode(next)
  }, [trackMode, setTrackMode])
  const cyclePreview = useCallback(() => {
    const next: 'camera-locked' | 'plane-anchored' =
      previewMode === 'camera-locked' ? 'plane-anchored' : 'camera-locked'
    setLocalPreviewMode(next)
    setPreviewMode(next)
  }, [previewMode, setPreviewMode])
  return (
    <div
      data-testid="debug-config-popover"
      style={{
        position: 'absolute',
        right: 0,
        bottom: 'calc(100% + 8px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: 'rgba(0, 0, 0, 0.9)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 10,
        padding: 10,
        boxShadow: '0 4px 12px rgba(0,0,0,0.45)',
        minWidth: 180,
      }}
    >
      <button
        type="button"
        onClick={cycleTrack}
        data-testid="debug-track-toggle"
        style={{
          background: 'rgba(255,255,255,0.08)',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 12,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {trackMode === 'plane' ? 'PLANE' : 'WORLD'}
      </button>
      <button
        type="button"
        onClick={cyclePreview}
        data-testid="debug-preview-toggle"
        style={{
          background: 'rgba(255,255,255,0.08)',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 12,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        PREV: {previewMode === 'camera-locked' ? 'CAMERA-LOCKED' : 'PLANE-ANCHORED'}
      </button>
    </div>
  )
}
